/**
 * Moving a customer who is still waiting: to another service's line, or to the
 * front or back of their own, before any desk has called them.
 *
 * The rules are the server's (engine/flow.go, Move) and are mirrored here, where a
 * test runs them, so the panel never offers a move the server will refuse. A move
 * targets a service, never a queue: the branch's routing chooses the line, as it
 * does at issue.
 */
import type { Ticket } from './types'
import { t, type StaffLanguage } from './staffText'
import { placementBody, type Placement } from './transfer'

export interface MoveChoice {
  serviceId: string
  placement: Placement
  /** 'desk' sends them to one desk, which calls them first; absent is a service. */
  to?: 'service' | 'desk'
  counterId?: string
}

export function currentServiceId(ticket: Pick<Ticket, 'transactions' | 'currentTransaction'>): string {
  return ticket.transactions[ticket.currentTransaction]?.serviceId ?? ''
}

/** Why this move would be refused, in words, or '' when it would not. `front` is
 *  whether the ticket is already at the front of its line. */
export function whyNotMove(currentService: string, choice: MoveChoice, front: boolean, lang: StaffLanguage): string {
  if (choice.to === 'desk') {
    return choice.counterId ? '' : t('floor.chooseDesk', lang)
  }
  if (!choice.serviceId) return t('floor.chooseServiceToWaitFor', lang)
  if (choice.serviceId === currentService && choice.placement === 'front' && front) {
    return t('floor.alreadyWaitingThere', lang)
  }
  return ''
}

export function moveBody(choice: MoveChoice): {
  serviceId?: string
  counterId?: string
  placement?: Placement
} {
  if (choice.to === 'desk') {
    return { counterId: choice.counterId ?? '', ...placementBody(choice.placement) }
  }
  return { serviceId: choice.serviceId, ...placementBody(choice.placement) }
}
